import { DropdownOption, mapToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import { generateRadioButtonOptionsFromEnum } from 'components/final-form/radioUtils';
import React, { Component } from "react";
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { Button, Grid, Header, RadioProps } from 'semantic-ui-react';
import { PrinterConfigDto, UpsertPrinterConfigDto } from 'ts-types/api.types';
import axios, { CancelTokenSource } from 'axios';
import FormContainer from 'components/layout/FormContainer';
import { PrintOrientation } from 'ts-types/api.enums';
import PraxFormScrollableContainer from 'components/layout/PraxFormScrollableContainer';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import { editPrinter, savePrinter } from 'services/printerConfigServices';
import Radio from 'components/final-form/Radio';
import FormContent from 'components/layout/FormContent';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';

interface Props extends MessageSourceProps {
  printerId?: number,
  printer: UpsertPrinterConfigDto,
  printers: Array<PrinterConfigDto>,
  modalOpen: boolean,
  onCancel: () => void,
  onSave: () => void
}

interface State {
  orientations: Array<RadioProps>,
  printerOptions: Array<DropdownOption>,
  paperSizeOptions: Array<DropdownOption>,
  sourceOptions: Array<DropdownOption>,
  errorMessage?: string,
  cancelTokenSource: CancelTokenSource
}

class UpsertPrinterConfigForm extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {getMessage, printers, printer} = props;
    const cancelTokenSource = axios.CancelToken.source();

    let printersOptions = mapToDropdownOptionArray(printers, "printername");
    let paperSizeOptions = mapToDropdownOptionArray(printers, "papersize");
    let sourceOptions = mapToDropdownOptionArray(printers, "firstorig");

    if(printer && printer.printername) {
      printersOptions.push({
        value: printer.printername,
        key: printer.printername,
        text: printer.printername
      });

    }
      if(printer && printer.papersize) {
        paperSizeOptions.push({
          value: printer.papersize,
          key: printer.papersize,
          text: printer.papersize
        });

      }
        if(printer && printer.firstorig){
          sourceOptions.push({
            value: printer.firstorig,
            key: printer.firstorig,
            text: printer.firstorig
          });

    }

    this.state = {
      orientations: generateRadioButtonOptionsFromEnum(Object.values(PrintOrientation), "orientation", getMessage),
      printerOptions: printersOptions,
      paperSizeOptions: paperSizeOptions,
      sourceOptions: sourceOptions,
      cancelTokenSource
    }
  };

  componentWillUnmount(): void {

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  handleSubmit = async (values: Partial<UpsertPrinterConfigDto>) => {
    const {printerId, getMessage, onSave} = this.props;

    const handleError = () => {
      this.setErrorMessage(getMessage("error.general"))
    };

    if (printerId) {
      await editPrinter(printerId, values)
          .then(onSave)
          .catch(handleError)
    } else {
      await savePrinter(values)
          .then(onSave)
          .catch(handleError)
    }
  };

  render(): React.ReactNode {

    const {printer} = this.props;

    return (
        <FinalForm
            onSubmit={this.handleSubmit}
            initialValues={printer}
            subscription={{pristine: true, submitting: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, submitting}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCancel} = this.props;
    const {orientations, errorMessage, printerOptions, paperSizeOptions, sourceOptions} = this.state;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              <PraxFormScrollableContainer height="600px">
                <PraxFormGrid>
                  {errorMessage
                  && (
                      <Grid.Row>
                        <Grid.Column>
                          <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                            {errorMessage}
                          </StyledErrorMessage>
                        </Grid.Column>
                      </Grid.Row>
                  )
                  }
                  <Grid.Row>
                    <Grid.Column>
                      <Header as="h5">{getMessage("printer.printer")}</Header>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={3}>
                      {getMessage("name")}:
                    </Grid.Column>
                    <Grid.Column width={4}>
                      <Field
                          name="name"
                          component={Input}
                      />
                    </Grid.Column>
                    <Grid.Column width={10}>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={3}>
                      {getMessage("printer.printerName")}:
                    </Grid.Column>
                    <Grid.Column width={7}>
                      <Field
                          name="printername"
                          component={Select}
                          options={printerOptions}
                          clearable
                          disabled={true}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>
                      <Header as="h5">{getMessage("printer.orientation")}</Header>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={3}>
                      <Field
                          name="orientation"
                          component={Radio}
                          radioDefinition={orientations[0]}
                      />
                    </Grid.Column>
                    <Grid.Column width={3}>
                      <Field
                          name="orientation"
                          component={Radio}
                          radioDefinition={orientations[1]}
                      />
                    </Grid.Column>
                    <Grid.Column width={10}>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>
                      <Header as="h5">{getMessage("printer.paperSize")}</Header>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>
                      <Field
                          name="papersize"
                          component={Select}
                          options={paperSizeOptions}
                          disabled={true}
                          clearable
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2}>
                      {getMessage("printer.marginX")}:
                    </Grid.Column>
                    <Grid.Column width={4}>
                      <Field
                          name="marginx"
                          component={Input}
                      />
                    </Grid.Column>
                    <Grid.Column width={10}>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2}>
                      {getMessage("printer.marginY")}:
                    </Grid.Column>
                    <Grid.Column width={4}>
                      <Field
                          name="marginy"
                          component={Input}
                      />
                    </Grid.Column>
                    <Grid.Column width={10}>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>
                      <Header as="h5">{getMessage("printer.source")}</Header>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={3}>
                      {getMessage("printer.firstTrayOriginal")}
                    </Grid.Column>
                    <Grid.Column width={4}>
                      <Field
                          name="firstorig"
                          component={Select}
                          options={sourceOptions}
                          disabled={true}
                          clearable
                          fluid
                      />
                    </Grid.Column>
                    <Grid.Column width={9}>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={3}>
                      {getMessage("printer.nextTrayOriginal")}
                    </Grid.Column>
                    <Grid.Column width={4}>
                      <Field
                          name="nextorig"
                          component={Select}
                          options={sourceOptions}
                          disabled={true}
                          clearable
                          fluid
                      />
                    </Grid.Column>
                    <Grid.Column width={9}>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={3}>
                      {getMessage("printer.firstTrayCopy")}
                    </Grid.Column>
                    <Grid.Column width={4}>
                      <Field
                          name="firstcopy"
                          component={Select}
                          options={sourceOptions}
                          disabled={true}
                          clearable
                          fluid
                      />
                    </Grid.Column>
                    <Grid.Column width={9}>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={3}>
                      {getMessage("printer.nextTrayCopy")}
                    </Grid.Column>
                    <Grid.Column width={4}>
                      <Field
                          name="nextcopy"
                          component={Select}
                          options={sourceOptions}
                          disabled={true}
                          clearable
                          fluid
                      />
                    </Grid.Column>
                    <Grid.Column width={9}>
                    </Grid.Column>
                  </Grid.Row>
                </PraxFormGrid>
              </PraxFormScrollableContainer>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="submit" primary disabled={submitting}>
                {getMessage("button.save")}
              </Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}
              </Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };

}

export default withMessages(UpsertPrinterConfigForm);
